/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Logger } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { SearchService } from '@app/ui/search/search.service';
import { CurrentExamContextService } from '@app/common/current-exam-context.service';
import { DiConfigService } from '@app/di/di-config.service';
import { DiService } from '@app/di/di.service';
import { EventMessage, Events, EventService } from '@app/common/event';
import { MessageService } from '@core/lib/services/message.service';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { AppConfigService } from '@app/common/app-config.service';
import { NotifyService } from '@app/common/notify.service';
import { XmlTemplateManagerService } from '@app/di/channel/lineshaft/xml-template-manager.service';
import { TabContextService } from '@app/ui/client-cache/tab-context.service';
import { TranslateService } from '@ngx-translate/core';
import { ExamCacheService } from '@app/ui/client-cache/exam-cache.service';
import { TabWorkListService } from '@app/ui/tabs/tab-work-list.service';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { UtilService } from '@app/util/util.service';
import { appConstants } from '@app/common/app-constants';
import { ReportContextService } from '@app/di/integrations/reporting/report-context.service';
import { ReportSupportService } from '@app/di/integrations/reporting/report-support.service';
import { REPORT_STATUS, REPORTING_EVENT } from '@app/di/integrations/reporting/reporting-contants';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { SearchCriteriaType, SearchParams } from '@app/common/types/search-params';
import { DatepickerRangeOptions } from '@wfm/core';
import { ExternalAppContextService } from '@app/di/external-app-context.service';
import {
  PerformanceMonitorService,
  PerformanceSource
} from '@app/ui/masthead/performance-monitor/performance-monitor.service';

const log = new Logger('GenericReportingService');

@Injectable({
  providedIn: 'root'
})
export class GenericReportingService {
  private REPORTING_CONFIG;
  private closeViewerCallback;
  private checkViewerContextCallback;
  private callBackOnViewerClose;

  private commaDelimiter = ',';
  private dictationStatus = appConstants.dictationIntegrationStatus.None;
  private reportStatusFromPS;
  private reportAddendumStatus;
  private moduleName;
  private ownerInstance;

  constructor(
    private searchService: SearchService,
    private currentExamContext: CurrentExamContextService,
    private diConfigService: DiConfigService,
    private diService: DiService,
    private reportSupportService: ReportSupportService,
    private eventService: EventService,
    private messageService: MessageService,
    private integrationStatusService: IntegrationStatusService,
    private appConfigService: AppConfigService,
    private notify: NotifyService,
    private xmlTemplateManagerService: XmlTemplateManagerService,
    private tabContextService: TabContextService,
    private translate: TranslateService,
    private examCacheService: ExamCacheService,
    private reportContextService: ReportContextService,
    private externalAppContextService: ExternalAppContextService,
    private tabWorklistService: TabWorkListService,
    private preferences: UserPreferenceService,
    private utilService: UtilService
  ) {}

  initialize() {
    log.info('initialize generic reporting integration');
    this.REPORTING_CONFIG = this.diConfigService.REPORTING_CONFIG;
    this.moduleName = this.REPORTING_CONFIG.MODULENAME;
    this.ownerInstance = this.REPORTING_CONFIG.OWNERINSTANCE;

    // Subscribe to external app remove context event
    this.externalAppContextService.removeContext$.subscribe(accession => {
      this.cancelDifferentReportContext(accession);
    });
  }

  /**
   * Method to disable Dictation integration
   */
  private disableDictationIntegration() {
    this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
    if (
      this.utilService.getSystemProp()['REPORTING_SSO_LOGIN'] &&
      this.utilService.getSystemProp()['SSO_Flag'] &&
      this.utilService.getSystemProp()['SSO_Flag'] === 'true'
    ) {
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.dictationCannotLoginSSO'),
        this.translate.instant('di.desktopIntegration.titleDictateDisabledSSO')
      );
    } else {
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.dictationCannotLaunch'),
        this.translate.instant('di.desktopIntegration.titleDictateDisabled')
      );
    }
    this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
    // Clear report queue on disabling the dictation integration
    this.reportSupportService.clearMessageQueue();
    // Release read lock for the active exam
    this.reportSupportService.releaseReadLock();
  }

  setCloseViewerCallback(callback) {
    this.closeViewerCallback = callback;
  }

  /**
   * Method to set check viewer context callback
   */
  setCheckViewerContextCallback(callback) {
    this.checkViewerContextCallback = callback;
  }

  /**
   * Method to execute viewer closed callback function
   */
  invokeViewerClosedCallback() {
    if (!!this.callBackOnViewerClose) {
      log.info('invokeViewerClosedCallback - this.callBackOnViewerClose executing');
      this.callBackOnViewerClose();
      this.callBackOnViewerClose = undefined;
    }
  }

  /**
   * Method to validate configurations
   */
  isDictationConfigValid() {
    log.info("isDictationConfigValid - ModuleName :'" + this.moduleName + "'");
    let configStatus = true;
    if (UtilService.isEmpty(this.moduleName)) {
      log.error('isDictationConfigValid - Module name is empty');
      configStatus = false;
    } else if (_.isUndefined(this.REPORTING_CONFIG.APPPATH)) {
      log.error('isDictationConfigValid - Application path undefined');
      configStatus = false;
    } else if (UtilService.isEmpty(this.REPORTING_CONFIG.APPPATH)) {
      log.error('isDictationConfigValid - Application path empty');
      configStatus = false;
    }
    if (!configStatus) {
      this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
      log.error(this.translate.instant('di.desktopIntegration.critical.incorrectDictateConfig'));
    }
    return configStatus;
  }

  /**
   * Method to parse generic reporting integration response
   */
  parseReportingTopicResponse(response, responseXML) {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseXML, 'text/xml');
      response.dataNodes = xmlDoc.getElementsByTagNameNS('http://ws.dotmed.gehc.com/dataexchange/', 'data');
      for (const dataNode of response.dataNodes) {
        // Take the name nodes from the xml
        const nameNode = dataNode.childNodes.item(1).childNodes[0].nodeValue;
        if (dataNode.childNodes.item(9) !== null && dataNode.childNodes.item(9).childNodes[0] !== undefined) {
          let nodeValue = dataNode.childNodes.item(9).childNodes[0].nodeValue;
          if (nameNode === 'OPERATION') {
            nodeValue = nodeValue.toString().replace(/\\/gi, appConstants.ESCAPE_BLACKSLASH);
            response.operationStatus = JSON.parse(nodeValue);

            UtilService.convertAddendum(response);
            UtilService.convertReportStatus(response);
            log.info('parseReportingTopicResponse:: OPERATION-->' + JSON.stringify(response.operationStatus));
          }
        }
      }
    } catch (error) {
      log.error('parseReportingTopicResponse failed. Error = ' + error);
    }
    return response;
  }

  /**
   * Method to get module name
   */
  getModuleName() {
    return this.moduleName + '*' + this.moduleName;
  }

  /**
   * Method to return this.dictationStatus
   */
  getDictationStatus() {
    return this.dictationStatus;
  }

  setDictationStatus(status) {
    this.dictationStatus = status;
  }

  /**
   * Method to subscribe required responses and events, load plugin
   */
  subscribeResponseAndEvents(loadInvokeAPICallback) {
    // Setting callback for setcontext timeout
    this.reportSupportService.setSetContextTimeoutCallback(this.handleSetContextTimeout);

    // Subscribe the load response from ReportingPlugin
    const loadResponse = this.xmlTemplateManagerService.getSubscribeXml(
      'LS_PLUGIN_LOAD_RESPONSE',
      this.getModuleName()
    );
    log.info('Subscribe the load response from ReportingPlugin');
    loadInvokeAPICallback(loadResponse);

    // Subscribe response from LineShaftPluginManager. If ReportingPlugin is missing DI receives
    // response only from LineShaftPluginManager.
    const loadResponseFromPluginManager = this.xmlTemplateManagerService.getSubscribeXml(
      this.ownerInstance + '.LS_PLUGIN_LOAD_RESPONSE',
      'LineShaftPluginManager'
    );
    log.info('Subscribe the load response from LineShaftPluginManager');
    loadInvokeAPICallback(loadResponseFromPluginManager);

    // Subscribe the SetContext response from ReportingPlugin
    const setContextResponse = this.xmlTemplateManagerService.getSubscribeXml(
      this.ownerInstance + '.LS_PLUGIN_SETCONTEXT_RESPONSE',
      this.getModuleName()
    );
    log.info('Subscribe the SetContext response from ReportingPlugin');
    loadInvokeAPICallback(setContextResponse);

    // Subscribe the reporting topic response from Reporting Application
    const reportingTopicResponse = this.xmlTemplateManagerService.getSubscribeXml(
      'LS_REPORTING_TOPIC_RESPONSE',
      'ReportingPlugin'
    );
    log.info('Subscribe the reporting topic response from Reporting Application');
    loadInvokeAPICallback(reportingTopicResponse);
  }

  /**
   * Method to handle load response from plugin
   */
  handlePluginLoadResponse(response) {
    if (response.resultValue === 'SUCCESS') {
      log.info('handlePluginLoadResponse : ReportingPlugin load success');
      this.integrationStatusService.setReportingPluginStatus(true);

      // Terminate Reporting Application after LineShaft restart to prevent connection issues
      if (this.reportSupportService.getLineShaftTerminateStatus()) {
        log.info('handlePluginLoadResponse : Terminating Reporting Application to prevent connection issues');
        if (!UtilService.isEmpty(this.REPORTING_CONFIG.APPPATH)) {
          const reportingAppName = this.REPORTING_CONFIG.APPPATH.split('\\').pop();
          const terminateXml = this.xmlTemplateManagerService.getTerminateXml(
            this.REPORTING_CONFIG.OWNERINSTANCE,
            reportingAppName
          );
          this.diService.invokeAPIWrapperExecute(terminateXml);
        }
        this.reportSupportService.setLineShaftTerminateStatus(false);
      }

      // Start the reporting application
      const params = {
        Action: appConstants.reportActionTypes.Start
      };
      const reportingStartXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
      this.reportSupportService.invokeAPIWrapper(reportingStartXML, '', appConstants.reportActionTypes.Start);
    }
    if (response.resultValue === 'FAILURE') {
      log.error('handlePluginLoadResponse : ReportingPlugin load failed');
      this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
    }
  }

  setContextAction(acc) {
    const currentAccession = this.reportContextService.getCurrentAccession();
    this.reportSupportService.clearMessageQueue();
    if (_.isUndefined(acc) || _.isEmpty(acc) || _.isNull(acc)) {
      if (currentAccession && currentAccession !== '') {
        this.notify.error(
          this.translate.instant('di.desktopIntegration.error.retryOpenReport').replace(/ACC#/g, currentAccession),
          this.translate.instant('di.desktopIntegration.title')
        );
        log.error('Report did not open. Accession #:' + currentAccession);
        this.integrationStatusService.setReportingHealthStatus(
          this.integrationStatusService.healthStatusEnum.lastActionFailed
        );
        // Publish report open failure event
        this.publishReportingEvent(REPORTING_EVENT.OPEN, { accession: currentAccession }, false, appConstants.FAILURE);
      } else {
        log.error('Report closed after timeout: ' + currentAccession);
        if (this.reportContextService.getReportContext() !== '') {
          // Publish report close success event
          const report = { accession: this.reportContextService.getReportContext() };
          this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.SUCCESS);
        }
        this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.ready);
      }
    } else if (acc && acc === currentAccession) {
      log.error('Report was opened after timeout. Accession #:' + currentAccession);
      // Publish report open success event
      this.publishReportingEvent(REPORTING_EVENT.OPEN, { accession: currentAccession }, false, appConstants.SUCCESS);
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.ready);
    } else if (acc && acc !== currentAccession) {
      if (this.diService.getApplicationContext() !== null) {
        this.notify.error(
          this.translate.instant('di.desktopIntegration.error.reportContextSwitchFailure').replace(/ACC#/g, acc),
          this.translate.instant('di.desktopIntegration.title')
        );
        log.error(
          'Reporting system failed to switch to the correct report. Accession #: ' +
            acc +
            ' was closed to prevent a context mismatch. If this error persists, contact your system ' +
            'administrator.'
        );
      }
      if (currentAccession && currentAccession !== '') {
        // Publish report open failure event
        this.publishReportingEvent(REPORTING_EVENT.OPEN, { accession: currentAccession }, false, appConstants.FAILURE);
      } else {
        if (this.reportContextService.getReportContext() !== '') {
          // Publish report close failure event
          const report = { accession: this.reportContextService.getReportContext() };
          this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.FAILURE);
        }
      }
      this.integrationStatusService.setReportingHealthStatus(
        this.integrationStatusService.healthStatusEnum.lastActionFailed
      );
      this.cancelDifferentReportContext(acc);
    }
  }

  handleCurrentContextResponse(response) {
    log.error('CurrentContextResponse Received::' + JSON.stringify(response.operationStatus));
    if (this.dictationStatus === appConstants.dictationIntegrationStatus.TimedOut) {
      this.setContextAction(response.operationStatus.AccessionNumber);
      this.dictationStatus = appConstants.dictationIntegrationStatus.Enabled;
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.ready);
    }
  }
  /**
   * Method to handle reporting topic success response
   */
  async handleSuccessReportingResponse(response) {
    log.info('handleSuccessReportingResponse - Action : ' + JSON.stringify(response.operationStatus));
    if (response.operationStatus.Action === appConstants.reportActionTypes.Start) {
      const systemProps = this.utilService.getSystemProp();
      const ssoFlag = systemProps['SSO_Flag'];
      const reportingSSOLogin = systemProps['REPORTING_SSO_LOGIN'];

      const isSSOFlagAbsentOrFalse = !ssoFlag || ssoFlag === 'false';
      const isReportingSSOLoginFalse = ssoFlag === 'true' && reportingSSOLogin === 'false';

      if (isSSOFlagAbsentOrFalse || isReportingSSOLoginFalse) {
        this.startLoginInterval();
      }
    } else if (
      response.operationStatus.Action === appConstants.reportActionTypes.SaveReport ||
      response.operationStatus.Action === appConstants.reportActionTypes.CancelReport
    ) {
      // If bidirectional context setting is in progress, wait until it finishes
      // to prevent report save before report meta data creation.
      if (this.reportSupportService.getBidirectionalPromise()) {
        await this.reportSupportService.getBidirectionalPromise();
      }
      this.reportSaveOrDiscardEventHandler(response);
    } else if (response.operationStatus.Action === appConstants.reportActionTypes.Login) {
      // Check NPR authentication status to enable or disable the integration.
      this.enableIntegrationAndSetContext();
    } else if (response.operationStatus.Action === appConstants.reportActionTypes.Terminate) {
      this.reportingTerminateEventHandler();
    } else if (response.operationStatus.Action === appConstants.reportActionTypes.OpenReport) {
      if (response.operationStatus.Source !== 'WFM') {
        this.reportOpenEventHandler(response);
      } else {
        PerformanceMonitorService.stop(PerformanceSource.REPORT, response.operationStatus.AccessionNumber);
        // Publish report open success event
        const report = {
          accession: response.operationStatus.AccessionNumber,
          status: UtilService.getExamStatusToUpdate(response.addendumStatus, response.reportStatus)
        };
        this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, response.operationStatus.Result);
        // Create report meta data in data base if not present.
        const examContext = this.currentExamContext.getFullContext();
        const accession = this.reportContextService.getCurrentAccession();
        if (examContext && examContext.keys.procedureAccessionNo === accession) {
          const reportId = examContext.keys.reportId ? examContext.keys.reportId : null;
          if (reportId === null) {
            this.reportSupportService.handleCreateReport(examContext.id, accession);
          } else {
            this.reportContextService.setNPRContext(reportId, examContext.id, accession);
          }
        }
        this.reportContextService.setReportContext(accession);
        // Acquire read lock for associated exams after successful report open
        this.acquireReadLockForAssociatedExams(examContext?.id);
      }
    } else {
      log.info(
        'handleSuccessReportingResponse - actions that do not need any specific handling' +
          response.operationStatus.Action
      );
    }
    this.setReportingHealth(response);
  }

  /**
   * reportOpenEventHandler - Handler for REPORT_OPENNED event from Reporting plugin
   * Reporting plugin trigger this event only for manual report launch
   */
  reportOpenEventHandler(response) {
    if (this.dictationStatus !== appConstants.dictationIntegrationStatus.Disabled) {
      const accessionNumber = response.operationStatus.AccessionNumber;
      if (!_.isUndefined(accessionNumber) && !_.isEmpty(accessionNumber)) {
        const accessions = accessionNumber.split(',');
        const primary = accessions[0];
        accessions.splice(0, 1);
        this.reportContextService.setReportContext(primary);
        this.reportSupportService.setBidirectionalContext(primary).then(
          _ => {
            log.info('reportOpenEventHandler - set context from GR success for accession: ' + accessionNumber);
            // Publish report open success event
            const report = {
              accession: response.operationStatus.AccessionNumber,
              status: UtilService.getExamStatusToUpdate(response.addendumStatus, response.reportStatus)
            };
            this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, response.operationStatus.Result);
          },
          error => {
            if (error === appConstants.FOUND_NONE) {
              this.showExamNotFoundModal();
            } else if (error === appConstants.MAX_TAB_ERROR) {
              this.cancelDifferentReportContext(accessionNumber);
            }
            log.error(
              'reportOpenEventHandler - set context from GR failed for accession: ' +
                accessionNumber +
                ', ERROR: ' +
                error
            );
          }
        );
      }
    }
  }

  showExamNotFoundModal(): void {
    this.notify.critical(
      this.translate.instant('collaboration.openlink.modal.recordNotAccessibleMessage'),
      this.translate.instant('collaboration.openlink.modal.recordNotAccessibleTitle')
    );
  }

  setReportingHealth(response) {
    if (
      response.operationStatus.Action !== appConstants.reportActionTypes.ForceMinimize &&
      response.operationStatus.Action !== appConstants.reportActionTypes.Minimize &&
      response.operationStatus.Action !== appConstants.reportActionTypes.Start &&
      response.operationStatus.Action !== appConstants.reportActionTypes.Login
    ) {
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.ready);
    } else if (response.operationStatus.Action === appConstants.reportActionTypes.Terminate) {
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
    }
  }

  enableIntegrationAndSetContext() {
    this.dictationStatus = appConstants.dictationIntegrationStatus.Enabled;
    this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.ready);
    const tab = this.utilService.getActiveTab();
    if (tab) {
      this.eventService.sendEvent(new EventMessage(Events[tab.examContext], { workItem: tab.workItem }));
    } else {
      this.handleApplicationState();
    }
  }

  /**
   * Method to check open report response and current exam context, if there is mismatch send cancel and
   * force minmize xml
   */
  cancelDifferentReportContext(accessionNumber) {
    if (
      _.isEmpty(this.reportContextService.getCurrentAccession()) ||
      accessionNumber.indexOf(this.reportContextService.getCurrentAccession()) === -1
    ) {
      log.info('cancelDifferentReportContext - out of context, closing report accession number : ' + accessionNumber);
      const params = {
        Action: appConstants.reportActionTypes.CancelReport,
        AccessionNumber: accessionNumber
      };
      log.info('cancel report for accession : ' + params.AccessionNumber);
      const cancelReportXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
      this.reportSupportService.invokeAPIWrapper(cancelReportXML, '', appConstants.reportActionTypes.CancelReport);
      this.handleApplicationState();
    }
  }

  /**
   * Reporting terminate event handler
   *  1. Display error message and disable the integration
   *  2. Save and close viewer in context
   */
  reportingTerminateEventHandler() {
    if (!this.diService.isLogoutInitiated()) {
      log.info('reportingTerminateEventHandler, shows re-launch confirmation dialog');
      if (this.dictationStatus !== appConstants.dictationIntegrationStatus.Disabled) {
        // To keeps the dictation disabled temporarily, to prevent any xml being send from WFMP.
        // Enables dictation based on user input
        this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
        this.reportContextService.setReportContext('');
        this.reportSupportService.showReportingCloseConfirmationDialog().then(data => {
          if (data === appConstants.YES) {
            this.sendReportingRelogin();
          } else {
            this.showReportingDisabledNotification();
          }
        });
      }
    }
  }

  /**
   * Shows dictation disabled critical notification and disable dictation on keep dictation off button click
   */
  showReportingDisabledNotification() {
    // If logout is initiated no need of viewer closing since it was already performed from cleanup method
    if (!this.diService.isLogoutInitiated()) {
      log.error('showReportingDisabledNotification, disable integration and send close viewer context');
      this.disableDictationIntegration();
      if (this.reportContextService.getCurrentRequestedProcId() !== '') {
        // Save and close viewer corresponding to the context
        this.closeViewerCallback('close', this.reportContextService.getCurrentRequestedProcId());
      }
    }
  }

  /**
   * Launch reporting application
   */
  sendReportingRelogin() {
    log.info('sendReportingRelogin, sending launch xml');
    if (!this.diService.isLogoutInitiated()) {
      // Start the reporting application
      const params = {
        Action: appConstants.reportActionTypes.Start
      };
      const reportingStartXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
      this.reportSupportService.invokeAPIWrapper(reportingStartXML, '', appConstants.reportActionTypes.Start);
    }
  }

  resetIntegration() {
    this.resetQueuesAndLastRequest();
    this.reportContextService.removeContext(this.reportContextService.getCurrentRequestedProcId());
    let params: any;
    params = {
      Action: appConstants.reportActionTypes.Logout,
      Username: this.REPORTING_CONFIG.USERNAME
    };
    log.error('The message queues are cleared on reset.');
    const logoutXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
    this.diService.invokeAPIWrapperExecute(logoutXML);
    params = {
      Action: appConstants.reportActionTypes.Terminate
    };
    const terminateXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
    this.diService.invokeAPIWrapperExecute(terminateXML);
    this.integrationStatusService.setReportingHealthStatus(
      this.integrationStatusService.healthStatusEnum.lastActionFailed
    );
    log.error('Log out and application termite actions are initiated.');
  }

  /**
   * Method to handle reporting topic failure response
   */
  handleFailureReportingResponse(response) {
    log.info('handleFailureReportingResponse - Action : ' + response.operationStatus.Action);
    if (
      response.operationStatus.Action === appConstants.reportActionTypes.Start ||
      response.operationStatus.Action === appConstants.reportActionTypes.Login
    ) {
      this.handleStartupFailure(response);
      // Returning from the to prevent the sending of minimize from handleApplicationState
      return;
    } else if (response.operationStatus.Action === appConstants.reportActionTypes.OpenReport) {
      this.handleOpenReportFailure(response);
    } else if (response.operationStatus.Action === appConstants.reportActionTypes.SaveReport) {
      this.handleSaveReportFailure(response);
    } else if (response.operationStatus.Action === appConstants.reportActionTypes.CancelReport) {
      this.handleCancelReportFailure(response);
    } else {
      log.error(
        'handleFailureReportingResponse - actions that do not need any specific handling, ' +
          'Action : ' +
          response.operationStatus.Action +
          ', Failure reason : ' +
          response.operationStatus.Description +
          ', Error Code : ' +
          response.operationStatus.ErrorCode
      );
    }
    // To force minimize the reporting application on failure
    this.handleApplicationState();
  }

  /**
   * Method to handle reporting topic response
   */

  handleReportingTopicResponse(response) {
    if (response.operationStatus.ErrorCode && response.operationStatus.ErrorCode === 'Pending_User_Action') {
      this.integrationStatusService.isPendingRequest(undefined);
    } else if (response.operationStatus.Result === 'SUCCESS') {
      log.info('handleReportingTopicResponse : Reporting Application ' + response.operationStatus.Action + ' Success');
      if (response.operationStatus.Action === appConstants.reportActionTypes.CurrentContext) {
        this.handleCurrentContextResponse(response);
      } else {
        this.handleSuccessReportingResponse(response);
      }
    } else {
      log.error('handleReportingTopicResponse : Reporting Application ' + response.operationStatus.Action + ' Failed');
      this.handleFailureReportingResponse(response);
    }
  }

  /**
   * Method to handle Reporting Application startup failure
   */
  handleStartupFailure(response) {
    log.error(
      'handleStartupFailure - Reporting Application ' +
        response.operationStatus.Action +
        ' failed, username : ' +
        response.operationStatus.Username +
        ' Failure reason : ' +
        response.operationStatus.Description +
        ', Error Code : ' +
        response.operationStatus.ErrorCode
    );
    this.disableDictationIntegration();
    log.info('handleStartupFailure start/login failure - Send force minimize request to ' + 'Reporting Application');
    this.sendForceMinimizeRequest();
  }

  sendForceMinimizeRequest() {
    const params = {
      Action: appConstants.reportActionTypes.ForceMinimize
    };
    log.info('sendForceMinimizeRequest - Force minimize Reporting Application');
    const forceMinimizeXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
    this.reportSupportService.invokeAPIWrapper(forceMinimizeXML, '', params.Action);
  }

  /**
   * Method to handle the open report failure
   */
  handleOpenReportFailure(response) {
    log.error(
      'handleOpenReportFailure - Open report failed, accession : ' +
        response.operationStatus.AccessionNumber +
        ', Failure reason : ' +
        response.operationStatus.Description +
        ', Error Code : ' +
        response.operationStatus.ErrorCode
    );
    this.notify.warning(
      this.translate.instant('di.desktopIntegration.warning.openReportFailed') +
        ' ' +
        response.operationStatus.AccessionNumber,
      this.translate.instant('di.desktopIntegration.title')
    );
    this.integrationStatusService.setReportingHealthStatus(
      this.integrationStatusService.healthStatusEnum.lastActionFailed
    );
    // Publish report open failure event
    const report = {
      accession: response.operationStatus.AccessionNumber
    };
    this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, response.operationStatus.Result);
    this.resetTabOnReportFailure(response.operationStatus.AccessionNumber);
    this.reportContextService.removeContext(this.reportContextService.getCurrentRequestedProcId());
  }

  /**
   * Method to handle the save report failure
   */
  handleSaveReportFailure(response) {
    log.error(
      'handleSaveReportFailure - Save report failed, accession : ' +
        response.operationStatus.AccessionNumber +
        ', Failure reason : ' +
        response.operationStatus.Description +
        ', Error Code : ' +
        response.operationStatus.ErrorCode
    );
    this.integrationStatusService.setReportingHealthStatus(
      this.integrationStatusService.healthStatusEnum.lastActionFailed
    );
    this.notify.warning(
      this.translate.instant('di.desktopIntegration.warning.saveReportFailed') +
        ' ' +
        response.operationStatus.AccessionNumber,
      this.translate.instant('di.desktopIntegration.title')
    );
    // Publish report close failure event
    let accession = response.operationStatus.AccessionNumber;
    if (accession.indexOf(this.commaDelimiter) !== -1) {
      accession = this.reportContextService.getReportContext();
    }
    const tab = this.utilService.getTabByAccession(accession);
    const report = { accession: response.operationStatus.AccessionNumber };
    const tabInterrupted = tab ? (tab.active ? false : true) : false;
    this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, tabInterrupted, response.operationStatus.Result);
  }

  /**
   * Method to handle the cancel report failure
   */
  handleCancelReportFailure(response) {
    log.error(
      'handleCancelReportFailure - Cancel report failed, accession : ' +
        response.operationStatus.AccessionNumber +
        ', Failure reason : ' +
        response.operationStatus.Description +
        ', Error Code : ' +
        response.operationStatus.ErrorCode
    );
    this.integrationStatusService.setReportingHealthStatus(
      this.integrationStatusService.healthStatusEnum.lastActionFailed
    );
    this.notify.warning(
      this.translate.instant('di.desktopIntegration.warning.closeReportFailed') +
        ' ' +
        response.operationStatus.AccessionNumber,
      this.translate.instant('di.desktopIntegration.title')
    );
    // Publish report close failure event
    const report = { accession: response.operationStatus.AccessionNumber };
    this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, response.operationStatus.Result);
  }

  /**
   * The responsibility of this method is to:
   * Send the login action after the specified time
   */
  startLoginInterval() {
    log.debug('startLoginInterval : Starting the login interval: ' + this.REPORTING_CONFIG.LOGININTERVAL);
    const startTimeout = setTimeout(() => {
      const password = this.diConfigService.getEncryptedPassword() as any;
      // Login reporting application
      let params = '';
      let isValid = true;
      let username = '';
      let xmlPassword = '';
      params = this.xmlTemplateManagerService.getLoginOperationXml();
      if (params === '') {
        log.error('genericpluginloginoperation.xml is missing in raddesktop template.');
        this.disableDictationIntegration();
      } else {
        try {
          params = JSON.parse(params);
          username = params['Username'];
          xmlPassword = params['Password'];

          const domain = this.getDomain(username);

          if (domain != '') {
            isValid = this.validateDomain(domain);
          }

          params['Username'] = username.replace(appConstants.PLACEHOLDER_USERNAME, this.REPORTING_CONFIG.USERNAME);
          params['Password'] = xmlPassword.replace(appConstants.PLACEHOLDER_PASSWORD, password?.encryptedPassword);
        } catch (error) {
          isValid = false;
          log.error('Kindly check the json in genericpluginloginoperation.xml file.');
          log.error(error);
        }
        if (isValid) {
          let reportingLoginXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
          reportingLoginXML = reportingLoginXML.replace('<p1:configkey/>', '');
          this.reportSupportService.invokeAPIWrapper(reportingLoginXML, '', appConstants.reportActionTypes.Login);
          this.dictationStatus = appConstants.dictationIntegrationStatus.LaunchApplicationInitiated;
        } else {
          this.disableDictationIntegration();
          log.error(
            'Domain is not properly configured. It should contain alpha numeric and special character hyphen (-) and should start and end with character only.'
          );
        }
      }
      clearTimeout(startTimeout);
    }, this.REPORTING_CONFIG.LOGININTERVAL);
  }

  /**
   * Get domain
   *
   */
  getDomain(username) {
    let domain = '';
    if (username != '' && username.indexOf(appConstants.BACKSLASH + appConstants.PLACEHOLDER_USERNAME) > -1) {
      domain = username.substring(0, username.indexOf(appConstants.BACKSLASH + appConstants.PLACEHOLDER_USERNAME));
    }
    return domain;
  }

  /**
   * Validate domain
   *
   */
  validateDomain(domain) {
    const regExp = new RegExp(appConstants.REGEX_ALPHA_NUMBERIC_HYPHEN);
    return regExp.test(domain);
  }

  /**
   * Report save or discard event handler
   *
   */
  async reportSaveOrDiscardEventHandler(response) {
    const accessionNumbers = response.operationStatus.AccessionNumber.split(',');
    const primaryIndex = accessionNumbers.indexOf(this.reportContextService.getReportContext());
    if (primaryIndex > -1) {
      response.accessionInSaveEvent = accessionNumbers[primaryIndex];
      response.isDiscardXML = response.operationStatus.Action === appConstants.reportActionTypes.CancelReport;
      this.reportContextService.setReportContext('');
      // Publish report close success event
      const tab = this.utilService.getTabByAccession(response.accessionInSaveEvent);
      let reportStatus = UtilService.getExamStatusToUpdate(response.addendumStatus, response.reportStatus);
      if (response.reportStatus === appConstants.ReportStatus.Discarded) {
        reportStatus =
          response.addendumStatus === appConstants.AddendumStatus.Yes ? REPORT_STATUS.FINAL : REPORT_STATUS.UNKNOWN;
      }
      const report = {
        accession: response.operationStatus.AccessionNumber,
        status: reportStatus
      };
      const tabInterrupted = tab ? (tab.active ? false : true) : false;
      this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, tabInterrupted, response.operationStatus.Result);
      // if report Id is empty and createReport is in progress, wait until it finishes.
      if (!this.reportContextService.getReportId() && this.reportSupportService.getCreateReportPromise()) {
        await this.reportSupportService.getCreateReportPromise();
      }
      // Setting examId and report id in response for future use
      const nprContext = this.reportContextService.getNPRContext();
      response.reportId = nprContext.reportId;
      response.primaryId = nprContext.examId;

      const workingItem = {
        reportId: nprContext.reportId,
        auditData: this.reportContextService.getNPRContextAuditData()
      };
      this.reportSupportService.workingItem = workingItem;

      // Delete report from database while discarding the report on Reporting Application
      if (response.reportStatus === appConstants.ReportStatus.Discarded) {
        this.reportSupportService.handleReportDiscard(response.addendumStatus);
      }
      this.reportContextService.clearNPRContext();
      this.associationWorkflowOrRegularWorkflow(response);
    }
  }

  /**
   * Association Workflow Or Regular Workflow based on Number of Accession Numbers
   */
  associationWorkflowOrRegularWorkflow(response) {
    this.reportStatusFromPS = response.reportStatus;
    this.reportAddendumStatus = response.addendumStatus;
    const index = response.operationStatus.AccessionNumber.indexOf(this.commaDelimiter);
    if (index !== -1) {
      this.associatedReportSavedOrClosed(response);
    } else {
      let currentContext = this.reportContextService.getContextCorrespondingToAccessionNum(
        response.operationStatus.AccessionNumber
      );
      if (currentContext === '') {
        currentContext = response.primaryId;
      }
      // Sync exams in database when report is saved.
      if (response.reportStatus > 0) {
        this.reportSupportService.syncSavedExamsInDB(currentContext, response.operationStatus.AccessionNumber);
      }
      this.reportSavedEventHandler(currentContext);
      this.publishEvents(response, currentContext, undefined);
    }
  }

  /**
   * Report saved event handler
   *
   */
  reportSavedEventHandler(contextId) {
    const tab = this.utilService.getActiveTab();
    const isExternalTabId = tab?.id ? (tab.id === appConstants.externalTabId ? true : false) : false;
    if (contextId === this.reportContextService.getCurrentRequestedProcId()) {
      this.reportContextService.removeContext(contextId);
      log.info('reportSavedEventHandler - InvokeAPI - hide report');
      if (!this.diService.isLogoutInitiated()) {
        if (!isExternalTabId) {
          this.handleApplicationState();
        }
      }
    } else {
      log.info(
        'reportSavedEventHandler - currentContext not equal to new context - param contextid = ' +
          contextId +
          ' currentContext = ' +
          this.reportContextService.getCurrentRequestedProcId()
      );
      if (this.reportContextService.getCurrentRequestedProcId() === '' && !this.diService.isLogoutInitiated()) {
        log.info('reportSavedEventHandler- currentContext is null');
        if (!isExternalTabId) {
          // make sure the report application is minimized, not hidden
          this.handleApplicationState();
        }
      }
    }
  }

  /**
   * Method to handle report save or auto save for associated exam
   */
  associatedReportSavedOrClosed(responseInfoParams) {
    const accNumArray = this.reportSupportService.getAccessionNumbersFromString(
      responseInfoParams.operationStatus.AccessionNumber,
      this.commaDelimiter
    );
    if (!_.isUndefined(accNumArray)) {
      // Get context ID and then remove context.
      let contextId = this.reportContextService.getContextCorrespondingToAccessionNum(
        responseInfoParams.accessionInSaveEvent
      );
      if (contextId === '') {
        contextId = responseInfoParams.primaryId;
      }
      this.handleAssociatedExams(responseInfoParams, accNumArray, contextId);
      this.reportSavedEventHandler(contextId);
    }
  }

  /**
   * Method to handle associated exams
   */
  handleAssociatedExams(responseInfoParams, accNumArray, primaryId) {
    // Do update associated exam's status first then the primary exam
    const index = accNumArray.indexOf(responseInfoParams.accessionInSaveEvent);
    if (index > -1) {
      accNumArray.splice(index, 1);
    }

    if (accNumArray.length > 0) {
      const promises = [];
      _.each(accNumArray, acc => {
        const searchParams = new SearchParams(SearchCriteriaType.ACCESSION);
        searchParams.accession = acc;
        searchParams.timeOfExam = {
          option: DatepickerRangeOptions.allDates,
          startTime: undefined,
          endTime: undefined
        };
        promises.push(this.searchService.getSearchExamsList(1, 50, searchParams));
      });

      if (promises.length > 0) {
        Promise.all(promises).then(
          result => {
            const associationList = {};
            _.each(result, (searchResult: any) => {
              _.each(searchResult, (exam: any) => {
                const id = exam.workItem.requestedProcedureId;
                associationList[exam.workItem.procedureAccessionNo] = {
                  isAssociated: 'Y',
                  requestedProcedureId: id
                };
              });
            });
            // Sync exams in database when report is saved.
            if (responseInfoParams.reportStatus > 0) {
              this.reportSupportService.syncSavedExamsInDB(
                primaryId,
                responseInfoParams.accessionInSaveEvent,
                associationList
              );
            }
            this.readAssociatedExamsWhenAssociationNotEnabled(responseInfoParams, primaryId, associationList);
          },
          error => {
            log.error('Error occurred while searching for an associated exam, Error: ' + error);
          }
        );
      }
    }
  }

  readAssociatedExamsWhenAssociationNotEnabled(responseInfoParams, primaryId, associationList) {
    const associationIds = _.pluck(_.values(associationList), 'requestedProcedureId');
    if (associationIds.length > 0) {
      this.eventService
        .sendEvent(
          new EventMessage(Events.read, new EventWorkItem(-1), {
            source: 'desktop-integration',
            associationList: associationIds
          })
        )
        .then(
          () => {
            log.debug(
              'readAssociatedExam - remote action read succeeded for' +
                ' Request ProcedureID ' +
                primaryId +
                ', Association List: ' +
                associationIds
            );
            this.tabContextService.saveAssociatedExamsInCache(primaryId, associationList);
            this.publishEvents(responseInfoParams, primaryId, associationIds);
          },
          error => {
            log.error(
              'readAssociatedExam - remote action read failed ' +
                ' with error: ' +
                error +
                ' for Requested ProcedureID: ' +
                primaryId +
                ', Association List: ' +
                associationIds
            );
          }
        );
    }
  }

  /**
   * Method to publish events on saving report in reporting application
   */
  publishEvents(responseInfo, primaryId, associationList) {
    try {
      if (associationList && associationList.length > 0) {
        this.examCacheService.setAssociateExamCache(primaryId);
        _.each(associationList, id => {
          this.examCacheService.setAssociateExamCache(id);
        });
      }
      // Getting requested procedure ID from Context-handler by passing accession number and
      // send that in publish event
      log.debug('Starting publishEvents responseInfo ' + responseInfo.toString() + ' contextId ' + primaryId);

      if (primaryId === '' && !this.diService.isLogoutInitiated()) {
        log.info('publishEvents - Context is empty.');

        if (!this.reportSupportService.isOpenReportRequestPresentInQueue()) {
          // To force minimize the reporting application on contextId empty. No need to
          // clear current context here as it is already done.
          log.info('isReportClosedEvent - Context is empty. Sent hide request to ReportingPlugin');
          this.handleApplicationState();
        }
        return;
      }
      // Save action invoked during tab closure, identified by checking the tab is present or not
      if (
        responseInfo.operationStatus.Action === appConstants.reportActionTypes.SaveReport &&
        !this.utilService.getTabByAccession(responseInfo.accessionInSaveEvent)
      ) {
        log.info('publishEvents : Save report on tab close workflow');
        if (UtilService.isNonAddendumReport(responseInfo)) {
          this.performActionInternal(
            'suspend',
            this.getWorkItemForAssociatedExams(primaryId, responseInfo.reportId, associationList)
          );
        }
        return;
      }

      // Publish readAndCloseExam event if status is
      // WetRead/Draft/PendingCorrection/Corrected/PendingSignature/Final
      // else publish READ_CLOSE_DISCARD event
      if (UtilService.isNonAddendumReport(responseInfo)) {
        this.publishEventForNonAddendum(responseInfo, primaryId, associationList);
      } else if (UtilService.isNonFinalAddendumReport(responseInfo)) {
        this.publishEventForNonFinalAddendum(responseInfo, primaryId, associationList);
      } else if (
        appConstants.AddendumStatus.Yes === responseInfo.operationStatus.Addendum &&
        appConstants.ReportStatus.Final === responseInfo.operationStatus.Status &&
        !responseInfo.isDiscardXML
      ) {
        this.performAction(
          'close',
          this.getWorkItemForAssociatedExams(primaryId, responseInfo.reportId, associationList)
        );
      } else {
        log.info('publishEvents: report is discarded');
        if (responseInfo.operationStatus.Source !== 'WFM') {
          this.performAction(
            'closediscard',
            this.getWorkItemForAssociatedExams(primaryId, responseInfo.reportId, associationList)
          );
        }
      }
    } catch (error) {
      log.error('publish event failed: ' + error.description + ' for requested procedure ID : ' + primaryId);
    }
    log.debug('Exiting publishEvents');
  }

  getWorkItemForAssociatedExams(primary, reportId, associationList) {
    const workItem = new EventWorkItem(primary);
    workItem.associationList = associationList;
    workItem.reportId = reportId;
    return workItem;
  }

  /**
   * Method to publish event for non addendum report save/discard action in reporting application
   */
  publishEventForNonAddendum(responseInfo, primaryId, associationList) {
    if (responseInfo.operationStatus.Source !== 'WFM') {
      this.performAction(
        'close',
        this.getWorkItemForAssociatedExams(primaryId, responseInfo.reportId, associationList)
      );
    } else {
      // If report is closed without saving(by clicking on close button), no need to publish
      // READ_CLOSE_SAVE_FOR_LATER event.
      if (!responseInfo.isDiscardXML) {
        log.info('publishEventForNonAddendum - publishEventForNonAddendum - call suspend directly');
        // If the topic is not REPORT_SAVED then it will be suspend so execute READ_CLOSE_SAVE_FOR_LATER
        // event.
        this.performActionInternal(
          'suspend',
          this.getWorkItemForAssociatedExams(primaryId, responseInfo.reportId, associationList)
        );
      }
    }
  }

  /**
   * Method to publish event for non final addendum report save/discard action in reporting application
   */
  publishEventForNonFinalAddendum(responseInfo, primaryId, associationList) {
    log.info('publishEventForNonFinalAddendum - publishEventForNonFinalAddendum - call clear');
    if (primaryId !== undefined) {
      // No need to invoke clear action for addendum report on interrupted scenario(ReportSavedForLater)
      if (responseInfo.operationStatus.Source !== 'WFM') {
        this.performAction(
          'clear',
          this.getWorkItemForAssociatedExams(primaryId, responseInfo.reportId, associationList)
        );
      }
    }
  }

  performActionInternal(actionName, workItem) {
    if (appConstants.externalTabId !== workItem.id) {
      log.debug('Starting performActionInternal ' + JSON.stringify(workItem) + ' actionName ' + actionName);
      workItem.keys.source = 'desktop-integration';
      workItem.reportStatus = UtilService.getExamStatusToUpdate(this.reportAddendumStatus, this.reportStatusFromPS);
      this.eventService.sendEvent(new EventMessage(Events[actionName], workItem)).then(
        () => {
          log.info(
            'performActionInternal remote action ' + actionName + ' succeeded for Request ProcedureID ' + workItem.id
          );
          if (workItem.reportStatus > 0) {
            this.reportSupportService.syncSavedExamsReportStatus(workItem);
          }
        },
        error => {
          log.error(
            'performActionInternal remote action ' +
              actionName +
              ' for failed with error: ' +
              error +
              'for Requested ProcedureID: ' +
              workItem.id
          );
        }
      );
      log.info('Exiting performActionInternal');
    } else {
      this.reportContextService.clearDxHubContext(appConstants.externalTabId);
      this.reportContextService.removeContext(appConstants.externalTabId);
      const tab = this.utilService.getTab(workItem.id);
      if (tab && tab.isExternalTab) {
        tab.externalTabMsgFailed = false;
        this.messageService.broadcast('removeExternalTab', tab.id);
      }
    }
  }

  performAction(actionName, workItem) {
    // Clear performance hook
    PerformanceMonitorService.clear(PerformanceSource.REPORT);
    log.info('performAction - action - ' + actionName + ' for ' + workItem.id);
    // Check whether viewer context is present and it is closable
    if (workItem.id !== '' && this.checkViewerContextCallback(workItem.id)) {
      this.callBackOnViewerClose = () => {
        log.info(
          'performAction -call performActionInternal on viewer close callback - action - ' +
            actionName +
            ' for ' +
            workItem.id
        );
        this.performActionInternal(actionName, workItem);
      };

      // Invoke close viewer callback to close the viewer with either save or discard option
      // If close viewer request failed cancel callback and send state handling request
      if (!this.closeViewerCallback(actionName, workItem.id)) {
        log.info('performAction -close viewer failed skip - action - ' + actionName);
        this.callBackOnViewerClose = undefined;
      }
    } else {
      log.info('performAction - call performActionInternal directly - action - ' + actionName + ' for ' + workItem.id);
      this.performActionInternal(actionName, workItem);
    }
  }

  /**
   * Function to handle ReportingPlugin specific LineShaft messages on handleLineShaftMessage event
   *
   */
  handleLineShaftMessage(response, responseXML) {
    if (response.topicName === 'LS_PLUGIN_LOAD_RESPONSE' && response.sourceName === this.getModuleName()) {
      this.handlePluginLoadResponse(response);
    } else if (
      (response.topicName === 'RadDesktop.LS_PLUGIN_SETCONTEXT_RESPONSE' ||
        response.topicName === 'RadDesktop.CTX_CTRL_MESSAGE_RESPONSE') &&
      response.sourceName === 'LineShaftPluginManager' &&
      response.resultValue === 'FAILURE' &&
      !this.diService.isLogoutInitiated()
    ) {
      // LineShaft crash - disable integration, no need to notify user from child controllers as it is handled in
      // parent controller's LSPluginManagerResponseHandler() function
      this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
      log.error('handleLineShaftMessage -LineShaft crash, desktop integration disabled');
      this.reportSupportService.clearMessageQueue();
    } else if (response.topicName === 'LS_REPORTING_TOPIC_RESPONSE' && response.sourceName === 'ReportingPlugin') {
      this.parseReportingTopicResponse(response, responseXML);
      this.handleReportingTopicResponse(response);
      this.triggerProcessQueueOnResponse(response);
    } else if (
      response.topicName === 'RadDesktop.LS_PLUGIN_SETCONTEXT_RESPONSE' &&
      response.sourceName === this.getModuleName()
    ) {
      this.handleSetcontextResponse(response);
    }
  }

  /**
   * Method to handle SetContext response
   */
  handleSetcontextResponse(response) {
    // Success response received from ReportingPlugin.
    if (response.resultValue === 'SUCCESS') {
      log.info('handleSetcontextResponse - Success response received from ReportingPlugin');
    } else {
      log.error(
        'handleSetcontextResponse - Failure response received from ReportingPlugin, ' +
          'Failure type : ' +
          response.failureType
      );
      if (response.failureType === 'LAUNCH_FAILURE') {
        log.error(
          'handleSetcontextResponse - Reporting Application launch failure, Please check the ' +
            'application path : ' +
            response.reportingAppPath
        );
        this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
        this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.critical.dictationCannotLaunch'),
          this.translate.instant('di.desktopIntegration.titleDictateDisabled')
        );
        // Clear report message queue on disabling the integration
        this.resetQueuesAndLastRequest();
      }
    }
  }

  resetQueuesAndLastRequest() {
    this.reportSupportService.clearMessageQueue();
    this.reportSupportService.clearMessageWaitingForResponse();
    // Clear the report setcontext timeout
    this.reportSupportService.clearSetContextTimeout();
  }

  /**
   * Method to handle launch readonly action.
   */
  launchReadonly(context) {
    try {
      log.info('launchreadonly event handler - Study Id : ' + context.id);
      // Save and close previous report opened and maintain the application state
      this.preview(context);
    } catch (error) {
      log.error('failed on launchreadonly action handler with error : ' + error);
    }
  }

  /**
   * Method to check whether integration is loading or not
   */
  isIntegrationLoading(currentAction) {
    let status = false;
    if (this.dictationStatus === appConstants.dictationIntegrationStatus.LaunchApplicationInitiated) {
      if (currentAction !== 'preview' && currentAction !== 'closediscard') {
        this.notify.warning(
          this.translate.instant('di.desktopIntegration.warning.dictationIsInitializing'),
          this.translate.instant('di.desktopIntegration.title')
        );
      }
      status = true;
    }
    return status;
  }

  /**
   * This method is invoked on activating an exam from a worklist.
   * The responsibility of this method is to: Open report based on user preference.
   *
   * target - selected exam's details
   */
  read(target) {
    try {
      // Interrupt scenario with previous report opened, sending save action before launching the current report
      if (
        this.reportContextService.getCurrentRequestedProcId() !== '' &&
        target.id !== this.reportContextService.getCurrentRequestedProcId()
      ) {
        log.info('read : Save report for previous context - ' + this.reportContextService.getCurrentAccession());
        const accessionNumberCombined = this.getAccessionInfo();
        const saveParams = {
          Action: appConstants.reportActionTypes.SaveReport,
          AccessionNumber: accessionNumberCombined
        };
        const saveReportXML = this.xmlTemplateManagerService.getReportingSetContextXml(saveParams);
        this.integrationStatusService.setReportingHealthStatus(
          this.integrationStatusService.healthStatusEnum.pendingResponse,
          saveParams.Action
        );
        this.reportSupportService.invokeAPIWrapper(saveReportXML, '', saveParams.Action);
      }
      if (
        target.id === this.reportContextService.getCurrentRequestedProcId() &&
        target.keys.procedureAccessionNo === this.reportContextService.getReportContext()
      ) {
        log.info('Report is in context, skip read action');
        return;
      }
      // Set current context
      this.reportContextService.setCurrentContext(target.id, target.keys.procedureAccessionNo);
      this.reportContextService.setNPRContextAuditData(target);
      const accessionNumberCombined = this.getAccessionInfo();
      // Open report on launch exam
      const params = {
        Action: appConstants.reportActionTypes.OpenReport,
        AccessionNumber: accessionNumberCombined
      };

      log.info('read : Open report for accession : ' + target.keys.procedureAccessionNo);
      const openReportXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
      this.integrationStatusService.setReportingHealthStatus(
        this.integrationStatusService.healthStatusEnum.pendingResponse,
        params.Action
      );
      this.reportSupportService.invokeAPIWrapper(openReportXML, params.AccessionNumber, params.Action);
    } catch (error) {
      log.error(
        'failed on read action handler - Accession : ' + target.keys.procedureAccessionNo + ' with error: ' + error
      );
    }
  }

  /**
   * This method is invoked on preview an exam from a worklist.
   * The responsibility of this method is to:
   *  1. Save and close report if open in Reporting Application.
   *  2. Force minimize reporting application if no report is opened.
   *
   * target - selected exam's details
   */
  preview(target) {
    try {
      const studyId = target.id;
      // If no report is opened, force minimize reporting application
      if (this.reportContextService.getReportContext() === '') {
        log.info('preview Accession : ' + target.keys.procedureAccessionNo + ' ; no current context');
        if (studyId === appConstants.externalTabId) {
          this.maximizeReportingApp();
        } else {
          this.handleApplicationState();
        }
      } else {
        if (target.keys.procedureAccessionNo !== this.reportContextService.getReportContext()) {
          // Report for a different context is showing
          log.info(
            'preview Accession : ' +
              target.keys.procedureAccessionNo +
              ' ; save report : ' +
              this.reportContextService.getReportContext()
          );
          const saveParams = {
            Action: appConstants.reportActionTypes.SaveReport,
            AccessionNumber: this.reportContextService.getReportContext()
          };
          const saveReportXML = this.xmlTemplateManagerService.getReportingSetContextXml(saveParams);
          this.reportSupportService.invokeAPIWrapper(saveReportXML, '', saveParams.Action);
          this.integrationStatusService.setReportingHealthStatus(
            this.integrationStatusService.healthStatusEnum.pendingResponse,
            saveParams.Action
          );
          this.reportContextService.setCurrentContext(target.id, '');
          if (studyId === appConstants.externalTabId) {
            this.maximizeReportingApp();
          }
        } else {
          // Preview study is same as currently launched study
          log.info('preview Accession : ' + target.keys.procedureAccessionNo + ' ; report is already open');
        }
      }
    } catch (error) {
      log.error('failed preview for accession ' + target.keys.procedureAccessionNo + ' with error: ' + error);
    }
  }

  maximizeReportingApp() {
    const params = {
      Action: 'MAXIMIZE'
    };
    log.info('maximizeReportingApp - Send ' + params.Action + ' request to Reporting Application');
    const maximizeXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
    this.reportSupportService.invokeAPIWrapper(maximizeXML, '', params.Action);
  }

  /**
   * Handle reporting applications state - signature queue mode
   */
  handleApplicationState() {
    // Minimize or force minimize reporting application based on current context
    const params = {
      Action:
        this.diService.getApplicationContext() === null
          ? appConstants.reportActionTypes.Minimize
          : appConstants.reportActionTypes.ForceMinimize
    };
    log.info('handleApplicationState - Send ' + params.Action + ' request to Reporting Application');
    const forceMinimizeXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
    this.reportSupportService.invokeAPIWrapper(forceMinimizeXML, '', params.Action);
  }

  /**
   * Subscribe the closediscard operation from diagnostic-hub.
   * The responsibility of this method is to: Discard the report
   *
   * target - selected exam's details
   */
  closeDiscardReport(target) {
    try {
      // Close request for a hidden tab
      if (
        this.reportContextService.checkReportInContext(target.id) &&
        this.reportContextService.getCurrentRequestedProcId() !== target.id
      ) {
        log.info('closeDiscard - close request for a hidden context is received study id : ' + target.id);
        this.reportContextService.removeContext(target.id);
        return false;
      }

      // Close request for exam not in context
      if (!this.reportContextService.checkReportInContext(target.id)) {
        log.info('closeDiscard - ' + target.id + ' ; Study not in context');
        if (this.reportContextService.getCurrentRequestedProcId() === '') {
          this.handleApplicationState();
        }
        return false;
      }
      let params;
      if (target.actionType == 'externalKeep') {
        log.info('closeDiscard - save report for accession : ' + target.keys.procedureAccessionNo);
        params = {
          Action: appConstants.reportActionTypes.SaveReport,
          AccessionNumber: target.keys.procedureAccessionNo
        };
      } else {
        log.info('closeDiscard - cancel report for accession : ' + target.keys.procedureAccessionNo);
        params = {
          Action: appConstants.reportActionTypes.CancelReport,
          AccessionNumber: target.keys.procedureAccessionNo
        };
      }
      const requestXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
      this.reportSupportService.invokeAPIWrapper(requestXML, '', params.Action);
      this.integrationStatusService.setReportingHealthStatus(
        this.integrationStatusService.healthStatusEnum.pendingResponse,
        params.Action
      );
      this.reportContextService.removeContext(target.id);
      return true;
    } catch (error) {
      log.error('failed on closeDiscard event with error : ' + error.description);
    }
  }

  public releaseControl(target: any): Promise<any> {
    const id = this.reportContextService.getContextCorrespondingToAccessionNum(target.accessionNo);
    if (
      id !== '' &&
      (target.releaseOption === 'save' || (target.releaseOption === 'auto' && this.isSaveOnReleaseControl()))
    ) {
      // Save report on close
      const params = {
        Action: appConstants.reportActionTypes.SaveReport,
        AccessionNumber: target.accessionNo
      };
      log.info('save report for accession : ' + target.accessionNo);
      const saveReportXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
      this.reportSupportService.invokeAPIWrapper(saveReportXML, '', appConstants.reportActionTypes.SaveReport);
      this.integrationStatusService.setReportingHealthStatus(
        this.integrationStatusService.healthStatusEnum.pendingResponse,
        params.Action
      );
      this.reportContextService.removeContext(id);
      return Promise.resolve(target);
    } else if (
      target.releaseOption === 'discard' ||
      (target.releaseOption === 'auto' && !this.isSaveOnReleaseControl())
    ) {
      return Promise.resolve(target);
    }
    return Promise.reject(target);
  }

  // #Added for take control
  private isSaveOnReleaseControl() {
    return this.preferences.getUserPreferenceFromCache('save-report-on-release-control') === 'save';
  }

  /**
   * Method to handle launchDictation action action.
   */
  launchDictation(target) {
    try {
      if (target.id === this.reportContextService.getCurrentRequestedProcId()) {
        log.info('Report is in context, skip launchDictation');
        return;
      }
      this.reportContextService.setCurrentContext(target.id, target.keys.procedureAccessionNo);
      this.reportContextService.setNPRContextAuditData(target);
      const accessionNumberCombined = this.getAccessionInfo(target.keys.procedureAccessionNo);
      // Open report on launch exam
      const params = {
        Action: appConstants.reportActionTypes.OpenReport,
        AccessionNumber: accessionNumberCombined
      };
      log.info('launchDictation - open report for accession : ' + target.keys.procedureAccessionNo);
      const openReportXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
      this.integrationStatusService.setReportingHealthStatus(
        this.integrationStatusService.healthStatusEnum.pendingResponse,
        params.Action
      );
      PerformanceMonitorService.start(
        PerformanceSource.REPORT,
        target.keys.procedureAccessionNo,
        target.keys.studyClickTime ? new Date(target.keys.studyClickTime) : undefined
      );
      this.reportSupportService.invokeAPIWrapper(
        openReportXML,
        target.keys.procedureAccessionNo,
        appConstants.reportActionTypes.OpenReport
      );
    } catch (error) {
      log.error('failed on launchDictation event with error : ' + error.description);
    }
  }

  /**
   * Method to acquire read lock for associated exams when launching dictation
   */
  private acquireReadLockForAssociatedExams(primaryId: string) {
    const associationIds = this.tabContextService.getAssociatedIds(primaryId);
    if (associationIds && associationIds.length > 0) {
      log.info('acquireReadLockForAssociatedExams - acquiring read lock for associated exams: ' + associationIds);
      this.eventService
        .sendEvent(
          new EventMessage(Events.read, new EventWorkItem(-1), {
            source: 'desktop-integration',
            associationList: associationIds
          })
        )
        .then(
          () => {
            log.debug('acquireReadLockForAssociatedExams - read lock acquired for associated exams: ' + associationIds);
          },
          error => {
            log.error(
              'acquireReadLockForAssociatedExams - failed to acquire read lock for associated exams: ' +
                associationIds +
                ' with error: ' +
                error
            );
          }
        );
    }
  }

  /**
   * Method to reset reporting application state after sending clear/close action.
   */
  resetApplicationState(context) {
    try {
      if (context.id === undefined) {
        log.error('resetApplicationState - workitem id is undefined');
        return;
      }
      this.reportSavedEventHandler(context.id);
    } catch (error) {
      log.error('failed on resetApplicationState with ' + error.description);
    }
  }

  /**
   * This method is responsible for processing the queued requests on receiving the response.
   *
   * responseInfo - response information
   */
  triggerProcessQueueOnResponse(responseInfo) {
    // If reporting topic response is received process the next request from queue
    log.info('triggerProcessQueueOnResponse - reporting topic response' + ' received process next request from queue');
    const messageWaitingForResponse = this.reportSupportService.getMessageWaitingForResponse();
    if (
      messageWaitingForResponse !== undefined &&
      messageWaitingForResponse.action === responseInfo.operationStatus.Action
    ) {
      if (messageWaitingForResponse.action === appConstants.reportActionTypes.OpenReport) {
        // Check whether the report opened for the same context
        if (responseInfo.operationStatus.AccessionNumber.indexOf(messageWaitingForResponse.accession) === -1) {
          return;
        }
      }
      log.info('triggerProcessQueueOnResponse - process next request from reporting queue');
      this.reportSupportService.clearMessageWaitingForResponse();
      // Clear the report setcontext timeout
      this.reportSupportService.clearSetContextTimeout();
      this.reportSupportService.processQueue();
    }
  }

  /**
   * The responsibility of this method is to:
   * 1. Display error message when timeout occurs
   * 2. Process next request from report message queue
   */
  handleSetContextTimeout = () => {
    log.info('handleSetContextTimeout : timeout occurred while receiving the report response');
    this.reportSupportService.clearMessageWaitingForResponse();
    this.dictationStatus = appConstants.dictationIntegrationStatus.TimedOut;
    const params = { Action: appConstants.reportActionTypes.CurrentContext };
    const contextXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
    this.reportSupportService.invokePriorityMessage(contextXML, '', appConstants.reportActionTypes.CurrentContext);
    this.integrationStatusService.setReportingHealthStatus(
      this.integrationStatusService.healthStatusEnum.pendingResponse
    );
    this.integrationStatusService.setPendingAction(appConstants.reportActionTypes.CurrentContext);
  };

  cleanup() {
    try {
      log.info('Start generic reporting integration cleanup');

      // During logout LineShaft WebSocket connection is closed without any delay. So even if we send the
      // SaveReport and Logout request form here it may not be processed by the Plugin. So removing the
      // SaveReport and Logout request from here, it is already handled in Plugin Close handler which is supposed
      // to execute either from the Close request sending below or from LineShaft WebSocket connection close flow.

      // When we implement Bi-directional workflow from Reporting Application, we may need changes in
      // ReportingPlugin to keep the accession number opened from Reporting Application to save the report
      // during logout.

      // Request to close ReportingPlugin should send even if plugin load is failed, so no need to check load
      // status.
      const closeXML = this.xmlTemplateManagerService.getPluginCloseXml(this.moduleName);
      this.diService.invokeAPIWrapperExecute(closeXML);
    } catch (error) {
      log.error('error during cleanup: ' + error.description);
      return false;
    }
  }

  examAssociation() {
    const accessionNumberCombined = this.getAccessionInfo();
    const params = {
      Action: appConstants.reportActionTypes.SETASSOCIATION,
      AccessionNumber: accessionNumberCombined
    };
    log.info(
      'examAssociation - Setting association for Primary Exam: ' +
        this.reportContextService.getCurrentAccession() +
        ', with Associated AccessionNumber: ' +
        params.AccessionNumber
    );

    const openReportXML = this.xmlTemplateManagerService.getReportingSetContextXml(params);
    this.integrationStatusService.setReportingHealthStatus(
      this.integrationStatusService.healthStatusEnum.pendingResponse,
      params.Action
    );

    this.reportSupportService.invokeAPIWrapper(openReportXML, accessionNumberCombined, params.Action);
  }

  private getAccessionInfo(primaryId?: string) {
    const accession = primaryId || this.reportContextService.getCurrentAccession() || '';
    const associatedExams = this.tabContextService.getAssociationListAccNumbers() || '';
    return accession + (associatedExams ? ',' + associatedExams : '');
  }

  private publishReportingEvent(eventName, report, tabInterrupted, result) {
    const reportStatus = report.status || REPORT_STATUS.UNKNOWN;
    this.reportSupportService.publishReportEvents(eventName, report.accession, reportStatus, tabInterrupted, result);
  }

  resetTabOnReportFailure(acc) {
    const tab = this.utilService.getTabByAccession(acc);
    if (tab) {
      this.reportSupportService.releaseReadLock(tab.id);
    }
  }
}
